import React, { useState, useRef, useCallback } from "react";
import { IonGrid, IonRow, IonCol, IonIcon } from "@ionic/react";
import { closeOutline, linkOutline, helpCircleOutline, chevronDownOutline } from "ionicons/icons";
import { Tooltip, Dropdown } from "antd";
import { useDispatch } from "react-redux";
import { useTutorial } from "../../Tutorial/TutorialProvider";
import Close from "./Close/Close";
import ExportFile from "./ExportFile";
import SaveIcon from "../../../assets/SvgEditor/Save.svg";
import ExportIcon from "../../../assets/SvgEditor/Export.svg";
import useSaveSVG from "../../../hooks/useSaveSvg";
import { useAuth } from "../../../contexts/AuthContext";
import { setCurrentFileWorkflow } from "../../../redux/slices/MyFiles";
import { isSvgTrace, isEmbroideryTrace } from "../../../config/appVariant";
import "./SVGTraceTopBar.css";

const SVGTraceTopBar = ({
  fileName,
  label = "SVG Editor",
  svgJoined,
  changesMade,
  setChangesMade,
  currentFile,
  rasterCommit,
  onCommitStart,
  leftContent,
  rightContent,
}) => {
  const { startTutorial } = useTutorial();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const isTouchDevice = 'ontouchend' in document;
  const isMobile = window.innerWidth <= 768;
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const { saveSVG, loading: savingInProgress } = useSaveSVG(svgJoined, currentFile);
  const [savedRecently, setSavedRecently] = useState(false);
  const savedTimerRef = useRef(null);
  const isOwner = user?.uid === currentFile?.ownerUid;

  const handleSave = useCallback(async () => {
    const success = await saveSVG();
    if (success !== false) {
      setChangesMade(false);
      setSavedRecently(true);
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSavedRecently(false), 2000);
    }
  }, [saveSVG, setChangesMade]);
  const currentWorkflow = currentFile?.workflow || "vector";

  return (
    <IonGrid className="svgtrace-topbar">
      <IonRow className="svgtrace-topbar-row">
        {/* Left actions */}
        {leftContent && !svgJoined && (
          <IonCol size="auto" className="svgtrace-topbar-left-actions">
            {leftContent}
          </IonCol>
        )}
        {svgJoined && (
          <IonCol size="auto" className="svgtrace-topbar-left-actions">
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Close
                changesMade={changesMade}
                svgJoined={svgJoined}
                currentFile={currentFile}
                styles={{
                  height: "26px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  background: isCloseHovered ? "#fee2e2" : "rgba(255,255,255,0.12)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: isCloseHovered ? "1px solid #fca5a5" : "1px solid rgba(255,255,255,0.18)",
                  padding: "0 8px",
                }}
                setIsCloseHovered={setIsCloseHovered}
              >
                <IonIcon
                  icon={closeOutline}
                  style={{
                    width: "14px",
                    height: "14px",
                    color: isCloseHovered ? "#dc2626" : "rgba(255,255,255,0.9)",
                    transition: "color 0.2s ease",
                    flexShrink: 0,
                  }}
                />
                <span style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: "Clash Grotesk",
                  color: isCloseHovered ? "#dc2626" : "rgba(255,255,255,0.9)",
                  transition: "color 0.2s ease",
                  lineHeight: 1,
                }}>
                  Close
                </span>
              </Close>
              {(!currentFile?.isPublic || isOwner) && (() => {
                // Derive save button visual state
                const isSaved = savedRecently && !changesMade && !savingInProgress;
                const isPending = changesMade && !savingInProgress;
                const isSaving = savingInProgress;

                const btnStyle = isSaved
                  ? { color: "#16a34a", background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.45)", cursor: "default" }
                  : isPending
                  ? { color: "#d97706", background: "rgba(217,119,6,0.12)", border: "1px solid rgba(217,119,6,0.55)", cursor: "pointer" }
                  : isSaving
                  ? { color: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", cursor: "wait" }
                  : { color: "rgba(255,255,255,0.3)", background: "transparent", border: "1px solid rgba(255,255,255,0.12)", cursor: "default" };

                const label = isSaved ? "Saved ✓" : isSaving ? "Saving…" : "Save";
                const iconFilter = isSaved
                  ? "brightness(0) saturate(100%) invert(48%) sepia(98%) saturate(400%) hue-rotate(95deg) brightness(95%) contrast(90%)"
                  : isPending
                  ? "brightness(0) saturate(100%) invert(60%) sepia(98%) saturate(800%) hue-rotate(5deg) brightness(100%) contrast(95%)"
                  : "brightness(0) invert(1) opacity(0.3)";

                return (
                  <Tooltip title={isPending ? "Unsaved changes" : isSaved ? "All changes saved" : "No changes"} placement="bottom">
                    <button
                      style={{
                        fontWeight: 600, fontFamily: "Clash Grotesk",
                        padding: "3px 10px", borderRadius: "6px",
                        fontSize: "11px", whiteSpace: "nowrap",
                        height: "26px",
                        display: "flex", alignItems: "center", gap: "4px",
                        transition: "all 0.2s ease",
                        ...btnStyle,
                      }}
                      disabled={!isPending}
                      onClick={isPending ? handleSave : undefined}
                      onMouseEnter={(e) => { if (isPending) e.currentTarget.style.background = "rgba(217,119,6,0.22)"; }}
                      onMouseLeave={(e) => { if (isPending) e.currentTarget.style.background = "rgba(217,119,6,0.12)"; }}
                    >
                      <img src={SaveIcon} alt="save" style={{ width: "11px", height: "11px", filter: iconFilter, transition: "filter 0.2s ease" }} />
                      {label}
                    </button>
                  </Tooltip>
                );
              })()}
            </div>
          </IonCol>
        )}

        {/* Center — Mode Toggle (only when both editors are available) */}
        <IonCol className="svgtrace-topbar-filename-section">
          {svgJoined && !isSvgTrace && !isEmbroideryTrace && (
            <div className="svgtrace-topbar-center">
              {isMobile ? (
                <Dropdown
                  menu={{
                    items: [
                      { key: "vector", label: "SVG Editor" },
                      { key: "stitch", label: "Stitch Editor" },
                    ],
                    selectedKeys: [currentWorkflow],
                    onClick: ({ key }) => dispatch(setCurrentFileWorkflow(key)),
                  }}
                  trigger={["click"]}
                >
                  <button style={{
                    display: "flex", alignItems: "center", gap: "4px",
                    background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "6px", padding: "3px 10px", cursor: "pointer",
                    color: "rgba(255,255,255,0.9)", fontSize: "11px", fontWeight: 600,
                    fontFamily: "Clash Grotesk",
                  }}>
                    {currentWorkflow === "stitch" ? "Stitch Editor" : "SVG Editor"}
                    <IonIcon icon={chevronDownOutline} style={{ fontSize: "10px", opacity: 0.7 }} />
                  </button>
                </Dropdown>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "2px", background: "rgba(255,255,255,0.06)", borderRadius: "6px", padding: "2px" }}>
                  <button
                    onClick={() => dispatch(setCurrentFileWorkflow("vector"))}
                    style={{
                      fontSize: "10px", fontWeight: 600, fontFamily: "Clash Grotesk",
                      padding: "3px 10px", borderRadius: "4px", cursor: "pointer", border: "none",
                      background: currentWorkflow === "vector" ? "rgba(255,255,255,0.15)" : "transparent",
                      color: currentWorkflow === "vector" ? "#fff" : "rgba(255,255,255,0.5)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    SVG
                  </button>
                  <button
                    onClick={() => dispatch(setCurrentFileWorkflow("stitch"))}
                    style={{
                      fontSize: "10px", fontWeight: 600, fontFamily: "Clash Grotesk",
                      padding: "3px 10px", borderRadius: "4px", cursor: "pointer", border: "none",
                      background: currentWorkflow === "stitch" ? "rgba(255,255,255,0.15)" : "transparent",
                      color: currentWorkflow === "stitch" ? "#fff" : "rgba(255,255,255,0.5)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Stitch
                  </button>
                </div>
              )}
            </div>
          )}
          {svgJoined && isEmbroideryTrace && (
            <div className="svgtrace-topbar-center">
              <span style={{ fontSize: "11px", fontWeight: 600, fontFamily: "Clash Grotesk", color: "rgba(255,255,255,0.9)" }}>
                Stitch Editor
              </span>
            </div>
          )}
          {svgJoined && isSvgTrace && (
            <div className="svgtrace-topbar-center">
              <span style={{ fontSize: "11px", fontWeight: 600, fontFamily: "Clash Grotesk", color: "rgba(255,255,255,0.9)" }}>
                SVG Editor
              </span>
            </div>
          )}
        </IonCol>

        {/* Right actions */}
        {rightContent && !svgJoined && (
          <IonCol size="auto" className="svgtrace-topbar-right-section">
            {rightContent}
          </IonCol>
        )}
        {svgJoined && (
          <IonCol size="auto" className="svgtrace-topbar-right-section">
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {isTouchDevice && (
                <button
                  onClick={() => startTutorial('converter', 17)}
                  style={{
                    color: "rgba(255,255,255,0.4)",
                    background: "transparent",
                    border: "none",
                    padding: "2px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    transition: "color 0.2s",
                  }}
                >
                  <IonIcon icon={helpCircleOutline} style={{ fontSize: "15px" }} />
                </button>
              )}
              {!isMobile && (
                <ExportFile
                  svgJoined={svgJoined}
                  buttonType="text"
                  variant="outlined"
                  defaultTab="share"
                  fileName={currentFile?.name}
                  rasterCommit={rasterCommit}
                  onCommitStart={onCommitStart}
                  onEditorModeChange={(mode) => dispatch(setCurrentFileWorkflow(mode))}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    <IonIcon icon={linkOutline} style={{ fontSize: "12px" }} />
                    Share
                  </span>
                </ExportFile>
              )}
              <ExportFile
                svgJoined={svgJoined}
                buttonType="primary"
                fileName={currentFile?.name}
                rasterCommit={rasterCommit}
                onCommitStart={onCommitStart}
                onEditorModeChange={(mode) => dispatch(setCurrentFileWorkflow(mode))}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                  <img src={ExportIcon} alt="export" style={{ width: "11px", height: "11px" }} />
                  Export
                </span>
              </ExportFile>
            </div>
          </IonCol>
        )}
      </IonRow>
    </IonGrid>
  );
};

export default SVGTraceTopBar; 
